import { OrderStatus, ProcedureStatus, ProcedureDocumentType, UserPlaceRole } from '@caresend/types';
import { getValidatedDraftOrder, encryptBooking, getBookingPath, encryptProcedure, getProcedurePath, encryptOrder, getOrderPath, getUnknownErrorMessage, generateID, addOrReplaceDocumentByType, initHealthGorillaInfo, objectMap, initElementTimestamp, getValidatedOrder, getValidatedUnencryptedBooking, getValidatedProcedure, getUserPath, deduplicateArrayByProperty, objectAssign, asyncTimeout, getUserHasPlaceRole, addUserPlaceRole } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import 'firebase/auth';
import '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { dbGroupSet, getValueOnce } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import { getStore } from '../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { isDashboardUser } from '../../helpers.js';

const isProcedureDocument = (document) => !Object.values(ProcedureDocumentType).includes(document.type);
/**
 * Splitting and merging procedures and bookings is required to visually
 * support a simplification of the order flow.
 *
 * The order flow is simplified by allowing the user to add multiple
 * products of different taskID's to a single medical order. These are split
 * to be saved in the database and then merged back together when the user
 * is viewing the order flow to continue the process properly.
 *
 * Saving to the database with split procedures allows us to support
 * draftWaypoint generation for a draftWaypoint since a taskID is required
 * to determine the minimum license requirements to generate a draftWaypoint.
 */
/**
 * Split procedures and bookings by products.
 */
const splitBookingProceduresByProducts = (draftBookings, draftProcedures) => {
    const store = getStore();
    const bookings = cloneDeep(draftBookings);
    const procedures = cloneDeep(draftProcedures);
    if (!bookings || !procedures)
        throw new Error('Missing bookings or procedures');
    Object.values(bookings !== null && bookings !== void 0 ? bookings : {}).forEach(({ documents: bookingDocuments, id: bookingID, labOrderID, patientShipmentTrackingNumber, procedures: procedureIDs, sampleShipmentTrackingNumber, specimenBags, }) => {
        const bookingProcedures = {};
        Object.keys(procedureIDs !== null && procedureIDs !== void 0 ? procedureIDs : {}).forEach((procedureID) => {
            const procedure = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID];
            if (!procedure)
                return;
            const addProcedureToBooking = () => {
                procedures[procedureID] = procedure;
                bookingProcedures[procedureID] = procedure;
            };
            const { products } = procedure;
            if (!products)
                return addProcedureToBooking();
            const productsByTaskID = {};
            Object.entries(products).forEach(([productID, productEntry]) => {
                var _a;
                const product = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.products[productID];
                if (!product)
                    return;
                const { taskID } = product;
                if (!taskID)
                    return;
                const taskProducts = productsByTaskID[taskID];
                if (!taskProducts)
                    productsByTaskID[taskID] = [productEntry];
                else
                    taskProducts.push(productEntry);
            });
            if (Object.values(productsByTaskID).length === 0)
                return addProcedureToBooking();
            Object.entries(productsByTaskID).forEach(([taskID, newProductsElements], index) => {
                var _a;
                const id = index === 0 ? procedureID : generateID();
                const newProducts = Object.fromEntries(newProductsElements.map((product) => ([product.id, product])));
                let documents = (_a = procedure.documents) !== null && _a !== void 0 ? _a : [];
                bookingDocuments === null || bookingDocuments === void 0 ? void 0 : bookingDocuments.forEach((bookingDocument) => {
                    if (!isProcedureDocument(bookingDocument))
                        return;
                    documents = addOrReplaceDocumentByType(documents, bookingDocument.file, bookingDocument.type, bookingDocument.info);
                });
                const healthGorillaInfo = labOrderID
                    ? initHealthGorillaInfo(labOrderID)
                    : undefined;
                const specimenBag = specimenBags === null || specimenBags === void 0 ? void 0 : specimenBags[taskID];
                const newProcedure = {
                    ...procedure,
                    id,
                    products: newProducts,
                    taskID,
                    ...(patientShipmentTrackingNumber && { patientShipmentTrackingNumber }),
                    ...(sampleShipmentTrackingNumber && { sampleShipmentTrackingNumber }),
                    ...(documents && { documents }),
                    ...(healthGorillaInfo && { healthGorillaInfo }),
                    ...(specimenBag && { specimenBag }),
                };
                procedures[id] = newProcedure;
                bookingProcedures[id] = newProcedure;
            });
        });
        const booking = bookings[bookingID];
        if (!booking)
            return;
        booking.procedures = objectMap(bookingProcedures, (procedure) => initElementTimestamp(procedure.id));
    });
    return { bookings, procedures };
};
/**
 * Merge procedures and bookings by products.
 * This is the inverse of splitBookingProceduresByProducts.
 */
const mergeBookingProceduresByProducts = (draftBookings, draftProcedures) => {
    var _a, _b;
    const bookings = (_a = cloneDeep(draftBookings)) !== null && _a !== void 0 ? _a : {};
    const procedures = (_b = cloneDeep(draftProcedures)) !== null && _b !== void 0 ? _b : {};
    Object.values(bookings !== null && bookings !== void 0 ? bookings : {}).forEach(({ procedures: procedureIDs, id: bookingID }) => {
        const bookingProcedures = {};
        const bookingProceduresByTaskTypeID = {};
        const productsByTaskTypeID = {};
        Object.keys(procedureIDs !== null && procedureIDs !== void 0 ? procedureIDs : {}).forEach((procedureID) => {
            var _a, _b, _c;
            const procedure = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID];
            if (!procedure)
                return;
            const { taskTypeID } = procedure;
            if (!taskTypeID) {
                bookingProceduresByTaskTypeID.noTaskID = [
                    ...((_a = bookingProceduresByTaskTypeID.noTaskID) !== null && _a !== void 0 ? _a : []),
                    procedure,
                ];
            }
            else {
                bookingProceduresByTaskTypeID[taskTypeID] = [
                    ...((_b = bookingProceduresByTaskTypeID[taskTypeID]) !== null && _b !== void 0 ? _b : []),
                    procedure,
                ];
                productsByTaskTypeID[taskTypeID] = {
                    ...((_c = productsByTaskTypeID[taskTypeID]) !== null && _c !== void 0 ? _c : {}),
                    ...procedure.products,
                };
            }
        });
        Object.entries(bookingProceduresByTaskTypeID).forEach(([taskTypeID, taskProcedures]) => {
            taskProcedures.forEach(({ id: procedureID }, index) => {
                if (taskTypeID === 'noTaskID') {
                    const procedure = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID];
                    if (!procedure)
                        return;
                    bookingProcedures[procedureID] = procedure;
                }
                else if (index === 0) {
                    const procedure = procedures === null || procedures === void 0 ? void 0 : procedures[procedureID];
                    if (!procedure)
                        return;
                    delete procedure.taskID;
                    procedure.products = productsByTaskTypeID[taskTypeID];
                    bookingProcedures[procedureID] = procedure;
                }
                else {
                    delete bookingProcedures[procedureID];
                    delete procedures[procedureID];
                }
            });
        });
        const booking = bookings[bookingID];
        if (!booking)
            return;
        booking.procedures = objectMap(bookingProcedures, (procedure) => initElementTimestamp(procedure.id));
    });
    return {
        bookings,
        procedures,
    };
};
/**
 * To improve our UX, we can display different loading messages in a set interval
 */
const setOrderFlowLoadingMessages = async (messages) => {
    const setLoading = async (message) => {
        getStore().commit('orderFlow/SET_LOADING', {
            isLoading: true,
            message,
        });
        await asyncTimeout(4000);
    };
    const initialPromise = Promise.resolve();
    await messages.reduce((previousMessage, currentMessage) => previousMessage.then(() => setLoading(currentMessage)), initialPromise);
};
const saveOrderAsDraft = async (draftOrder, draftBookings, draftProcedures, readyForScheduling = false, queuedForScheduling = false, patientInitiated = false) => {
    var _a;
    try {
        const store = getStore();
        const validatedDraftOrder = getValidatedDraftOrder(draftOrder);
        if (readyForScheduling) {
            validatedDraftOrder.readyForScheduling = readyForScheduling;
        }
        if (queuedForScheduling) {
            validatedDraftOrder.queuedForScheduling = queuedForScheduling;
        }
        if (patientInitiated) {
            const schedulingFlowDraftWaypointID = (_a = store.state.schedulingFlow.draftWaypoint) === null || _a === void 0 ? void 0 : _a.id;
            if (schedulingFlowDraftWaypointID) {
                validatedDraftOrder.draftWaypoints = {
                    [schedulingFlowDraftWaypointID]: true,
                };
            }
        }
        const updates = {};
        const bookingPromises = Object.values(draftBookings !== null && draftBookings !== void 0 ? draftBookings : {}).map(async (booking) => {
            const encryptedBooking = await encryptBooking(booking);
            updates[getBookingPath(booking.id)] = encryptedBooking;
        });
        const procedurePromises = Object.values(draftProcedures !== null && draftProcedures !== void 0 ? draftProcedures : {}).map(async (procedure) => {
            const encryptedProcedure = await encryptProcedure(procedure);
            updates[getProcedurePath(procedure.id)] = encryptedProcedure;
        });
        await Promise.all([
            ...bookingPromises,
            ...procedurePromises,
        ]);
        const encryptedOrder = await encryptOrder(validatedDraftOrder);
        updates[getOrderPath(encryptedOrder.id)] = encryptedOrder;
        await dbGroupSet(updates);
    }
    catch (error) {
        toastErrorAndReport(`Invalid order request: ${getUnknownErrorMessage(error)}`);
    }
};
const getPatientPlacesUpdate = async (patient, drawPlaceID) => {
    if (!isDashboardUser() || !drawPlaceID)
        return {};
    const patientPlaces = patient.places;
    const hasRole = getUserHasPlaceRole(patientPlaces, drawPlaceID, UserPlaceRole.PATIENT);
    if (hasRole)
        return {};
    const userPlace = addUserPlaceRole(patientPlaces, drawPlaceID, UserPlaceRole.PATIENT);
    return {
        [getUserPath(patient.id, `places/${drawPlaceID}`)]: userPlace,
    };
};
const createOrder = async (data, creator) => {
    try {
        const { bookings, draftWaypoints, order, procedures, } = data;
        const validatedOrder = {
            ...getValidatedOrder(order),
            /**
             * TODO: should we validate the draft waypoints based on the procedures
             * on the order before we store this data?
            */
            ...(draftWaypoints && { draftWaypoints }),
            status: OrderStatus.CREATED,
        };
        const validatedBookings = objectMap(bookings, getValidatedUnencryptedBooking);
        const validatedProcedures = objectMap(procedures, getValidatedProcedure);
        const updates = {};
        const bookingPromises = Object.values(validatedBookings).map(async (booking) => {
            const encryptedBooking = await encryptBooking(booking);
            updates[getBookingPath(booking.id)] = encryptedBooking;
        });
        const procedurePromises = Object.values(validatedProcedures).map(async (procedure) => {
            var _a, _b;
            if (procedure.diagnoses) {
                const patientID = (_a = validatedBookings === null || validatedBookings === void 0 ? void 0 : validatedBookings[procedure.bookingID]) === null || _a === void 0 ? void 0 : _a.patientID;
                if (!patientID)
                    return;
                const patient = await getValueOnce(getUserPath(patientID));
                if (patient) {
                    const existingDiagnoses = (_b = patient === null || patient === void 0 ? void 0 : patient.diagnoses) !== null && _b !== void 0 ? _b : [];
                    const updatedDiagnoses = existingDiagnoses.concat(procedure.diagnoses);
                    const updatedDiagnosesWithoutDuplicates = deduplicateArrayByProperty(updatedDiagnoses, 'secretID');
                    updates[getUserPath(patient.id, 'diagnoses')] = updatedDiagnosesWithoutDuplicates;
                    const patientPlacesUpdate = await getPatientPlacesUpdate(patient, order.drawPlaceID);
                    objectAssign(updates, patientPlacesUpdate);
                }
            }
            const encryptedProcedure = await encryptProcedure(procedure);
            encryptedProcedure.status = ProcedureStatus.CREATED;
            updates[getProcedurePath(procedure.id)] = encryptedProcedure;
        });
        await Promise.all([
            ...bookingPromises,
            ...procedurePromises,
        ]);
        const encryptedOrder = await encryptOrder(validatedOrder, creator);
        updates[getOrderPath(order.id)] = encryptedOrder;
        await dbGroupSet(updates);
    }
    catch (error) {
        toastErrorAndReport(`Invalid order request: ${getUnknownErrorMessage(error)}`);
    }
};

export { createOrder, mergeBookingProceduresByProducts, saveOrderAsDraft, setOrderFlowLoadingMessages, splitBookingProceduresByProducts };
